import { useQuery, useQueryClient } from "@tanstack/react-query";
import { collection, query, getDocs } from "firebase/firestore";
import { db } from "../../config/firebase";
import LocalAuthority from "../../models/LocalAuthority";
import { buildQueryConstraints, filterLocalAuthoritiesClientSide } from "./utils";

const QUERY_KEY = "local-authorities-dropdown";

/**
 * Hook for fetching local authorities for dropdowns. There is no pagination or total count returned.
 */
export function useLocalAuthoritiesForDropdowns({ filters } = {}, enabled = true) {
  return useQuery({
    queryKey: [QUERY_KEY, JSON.stringify(filters)],
    keepPreviousData: true,
    enabled,
    queryFn: async () => {
      try {
        const constraints = buildQueryConstraints(filters);
        const localAuthoritiesQuery = query(collection(db, "localAuthorities"), ...constraints);
        const snapshot = await getDocs(localAuthoritiesQuery);

        return snapshot.docs.map((doc) => new LocalAuthority({ ...doc.data(), id: doc.id }));
      } catch (error) {
        console.error("Failed to fetch local authorities for dropdown", error);
        throw error;
      }
    },
    select: (localAuthorities) => filterLocalAuthoritiesClientSide({ localAuthorities, search: filters.search }),
  });
}

/**
 * Hook that returns a function to invalidate the query and cause it to refetch.
 */
export const useInvalidateLocalAuthoritiesForDropdown = () => {
  const queryClient = useQueryClient();
  return (filters) => invalidateQuery({ queryClient, queryKey: QUERY_KEY, filters });
};
